import { ChatContextType } from '@/types/chat';

export interface ChatPanelTip {
  id: string;
  text: string;
}

export const CHAT_PANEL_TIPS: ChatPanelTip[] = [
  {
    id: 'customize-name-role',
    text: 'You can tell the Assistant to remember your name and role.',
  },
  {
    id: 'fraud-ring-detection',
    text: 'Ask the Assistant to be on the lookout for a new fraud ring pattern.',
  },
  {
    id: 'high-risk-signal',
    text: 'You can instruct the Assistant to always flag high risk if a certain signal is seen.',
  },
  {
    id: 'memory-feature',
    text: 'The Assistant can remember your preferences and instructions across conversations.',
  },
  {
    id: 'customer-search',
    text: 'Ask the Assistant to find similar documents based on the triggered fraud signals.',
  },
  {
    id: 'workspace-search',
    text: 'The Assistant can search across data for specific keywords.',
  },
  {
    id: 'transaction-analysis',
    text: 'The Assistant can add up amounts to ensure totals are correct.',
  },
];

export interface SuggestedChatPrompt {
  contextType: ChatContextType;
  prompt: string;
  expandedPrompt?: string;
}

export const DEFAULT_SUGGESTED_CHAT_PROMPTS_BY_CONTEXT: Record<
  ChatContextType,
  SuggestedChatPrompt[]
> = {
  [ChatContextType.Global]: [
    {
      contextType: ChatContextType.Global,
      prompt: 'What are your capabilities?',
    },
    {
      contextType: ChatContextType.Global,
      prompt: "I don't understand something",
    },
    {
      contextType: ChatContextType.Global,
      prompt: 'Remember something for future chats',
    },
  ],
  [ChatContextType.Customer]: [
    {
      contextType: ChatContextType.Customer,
      prompt: 'Run deeper analysis on this customer',
      expandedPrompt: 'Run a deeper analysis of this customer for fraud risk',
    },
    {
      contextType: ChatContextType.Customer,
      prompt: 'Find similar customers',
    },
    {
      contextType: ChatContextType.Customer,
      prompt: 'What are your capabilities?',
    },
  ],
  [ChatContextType.Document]: [
    {
      contextType: ChatContextType.Document,
      prompt: 'Run deeper analysis',
      expandedPrompt:
        'Run a deeper analysis of this document and customer for fraud risk',
    },
  ],
};

export const SUGGESTED_CHAT_PROMPTS_FOR_BANK_STATEMENTS: SuggestedChatPrompt[] =
  [
    {
      contextType: ChatContextType.Document,
      prompt: 'Run deeper analysis',
      expandedPrompt:
        'Run a deeper analysis of this document and customer for fraud risk',
    },
    {
      contextType: ChatContextType.Document,
      prompt: 'Does this contain gambling transactions?',
      expandedPrompt:
        'Does this bank statement contain gambling transactions? If found, also return the percentage, and the total monetary amount.',
    },
    {
      contextType: ChatContextType.Document,
      prompt: 'Are there any recurring transactions?',
      expandedPrompt:
        'Are there any recurring transactions in this statement? If found, display them as a list of bullet points.',
    },
  ];

// Non-ADP payslips have relatively worse parsing performance, so we need to
// ask simple, targeted questions compared to ADP payslips.
export const DEFAULT_SUGGESTED_CHAT_PROMPTS_FOR_PAYSLIPS: SuggestedChatPrompt[] =
  [
    {
      contextType: ChatContextType.Document,
      prompt: 'Run deeper analysis',
      expandedPrompt:
        'Run a deeper analysis of this document and customer for fraud risk',
    },
    {
      contextType: ChatContextType.Document,
      prompt: 'Calculate whether social security tax reconciles with wage',
      expandedPrompt:
        'Calculate whether the social security tax reconciles with the pay period wage. ' +
        'A 6.2% rate is applied to YTD gross wages, up to the wage base limit. ' +
        'This limit is $168,600 for 2024, $176,100 for 2025, and $184,500 for 2026.',
    },
  ];

export const SUGGESTED_CHAT_PROMPTS_FOR_ADP_PAYSLIPS: SuggestedChatPrompt[] = [
  {
    contextType: ChatContextType.Document,
    prompt: 'Run deeper analysis',
    expandedPrompt:
      'Run a deeper analysis of this document and customer for fraud risk',
  },
  {
    contextType: ChatContextType.Document,
    prompt: 'Does net pay reconcile with gross pay minus all deductions?',
    expandedPrompt:
      'Does the net pay reconcile with the gross pay minus all deductions? ' +
      'Only calculate amounts for the current pay period.',
  },
  {
    contextType: ChatContextType.Document,
    prompt: 'Calculate whether Medicare tax reconciles with wage',
    expandedPrompt:
      'Calculate whether Medicare tax reconciles with the pay period total taxable wage. ' +
      'The rate is 1.45% of gross wages. However, high earners pay at a rate of 2.35%.',
  },
];

export const SUGGESTED_CHAT_PROMPTS_FOR_W2_TAX_FORMS: SuggestedChatPrompt[] = [
  {
    contextType: ChatContextType.Document,
    prompt: 'Run deeper analysis',
    expandedPrompt:
      'Run a deeper analysis of this document and customer for fraud risk',
  },
  {
    contextType: ChatContextType.Document,
    prompt: 'Do social security wages reconcile with IRS percentages?',
    expandedPrompt:
      'Do social security wages reconcile with IRS percentages? ' +
      'Rate is at 6.2%, below the limit of $168,600 for 2024, ' +
      '$176,100 for 2025, and $184,500 for 2026.',
  },
];

export const SUGGESTED_CHAT_PROMPTS_FOR_1040_TAX_FORMS: SuggestedChatPrompt[] =
  [
    {
      contextType: ChatContextType.Document,
      prompt: 'Run deeper analysis',
      expandedPrompt:
        'Run a deeper analysis of this document and customer for fraud risk',
    },
    {
      contextType: ChatContextType.Document,
      prompt: 'Do the claimed credits align with income and dependents?',
    },
  ];

export const SUGGESTED_CHAT_PROMPTS_FOR_UTILITY_BILLS: SuggestedChatPrompt[] = [
  {
    contextType: ChatContextType.Document,
    prompt: 'Run deeper analysis',
    expandedPrompt:
      'Run a deeper analysis of this document and customer for fraud risk',
  },
  {
    contextType: ChatContextType.Document,
    prompt: 'Calculate whether total due matches usage',
    expandedPrompt:
      'Calculate whether total due matches usage times rate, plus additional taxes and fees, if present.',
  },
];

export const SUGGESTED_CHAT_PROMPTS_FOR_CHECKS: SuggestedChatPrompt[] = [
  {
    contextType: ChatContextType.Document,
    prompt: 'Run deeper analysis',
    expandedPrompt:
      'Run a deeper analysis of this document and customer for fraud risk',
  },
  {
    contextType: ChatContextType.Document,
    prompt: 'Extract the banking numbers',
    expandedPrompt:
      'Extract the bank account information from this check. ' +
      'For Canadian checks: transit number, institution number, and account number. ' +
      'For American checks: routing number and account number.',
  },
  {
    contextType: ChatContextType.Document,
    prompt: 'Does MICR line check number match printed number?',
  },
];
